import React, { useRef } from 'react';
import { createPortal } from 'react-dom';
import { type Icon } from '@tabler/icons-react';
import { getDropdownPosition } from './dropdownPosition';
import { useDropdownDismiss } from './useDropdownDismiss';
import './dropdown.css';

/**
 * Type A (two-line variant) — a right-aligned, content-width menu of command
 * actions, each with a leading Tabler icon, a primary label, and an optional
 * secondary description line. Unlike {@link ActionPicker} it drives one-shot
 * commands (Generate invoice, Create variation…) rather than selecting a value,
 * so there's no active/check state — each option carries its own `onSelect`.
 *
 * Portals to <body>, positions against `anchorRef`, dismisses on outside-click
 * + Escape. The on-system replacement for MUI `Menu`/`MenuItem` (skill §10).
 */
export interface ActionMenuOption {
  label: string;
  /** Secondary guidance line beneath the label. */
  description?: string;
  /** Leading icon — a tabler icon component. */
  Icon?: Icon;
  onSelect: () => void;
  /** Render after a divider, in the muted danger style. */
  destructive?: boolean;
}

interface ActionMenuProps {
  options: ActionMenuOption[];
  onClose: () => void;
  anchorRef: React.RefObject<HTMLElement>;
  /** Force opening above the trigger (e.g. triggers near the viewport bottom). */
  preferAbove?: boolean;
}

export const ActionMenu: React.FC<ActionMenuProps> = ({
  options,
  onClose,
  anchorRef,
  preferAbove,
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  useDropdownDismiss(dropdownRef, anchorRef, onClose);

  if (!anchorRef.current) return null;

  const position = getDropdownPosition(anchorRef.current, {
    preferAbove,
    align: 'right',
  });
  const normal = options.filter((o) => !o.destructive);
  const destructive = options.filter((o) => o.destructive);

  const renderItem = (option: ActionMenuOption, index: number) => {
    const ItemIcon = option.Icon;
    return (
      <div
        key={`${option.label}-${index}`}
        className={`sm4rt-dd__item sm4rt-dd__item--action ${
          option.destructive ? 'sm4rt-dd__item--danger' : ''
        }`}
        onClick={() => {
          option.onSelect();
          onClose();
        }}
      >
        {ItemIcon && (
          <ItemIcon className="sm4rt-dd__icon" size={16} stroke={1.5} />
        )}
        <span className="sm4rt-dd__action-text">
          <span className="sm4rt-dd__action-label">{option.label}</span>
          {option.description && (
            <span className="sm4rt-dd__action-desc">{option.description}</span>
          )}
        </span>
      </div>
    );
  };

  return createPortal(
    <div
      className="sm4rt-dd sm4rt-dd--menu sm4rt-dd--action"
      ref={dropdownRef}
      style={position}
    >
      <div className="sm4rt-dd__list">
        {normal.map(renderItem)}
        {destructive.length > 0 && <div className="sm4rt-dd__divider" />}
        {destructive.map((o, i) => renderItem(o, normal.length + i))}
      </div>
    </div>,
    document.body,
  );
};
